import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Button } from "@mui/material";

import { Loading } from "commons";
import { useComponentState } from "commons/src/helpers/CustomHooks";
import { DONE } from "commons/src/helpers/RequestHelpers";
import { validate } from "commons/src/helpers/PasswordValidation";

import { ERROR as ERROR_SEVERITY, INFO as INFO_SEVERITY } from "../../helpers/ErrorHelpers";
import { Message } from "../../components/Message";
import { Accordion } from "../../components/Accordion";
import { isSubmitDisabled } from "../../helpers/ChangePasswordHelpers";
import {
    AuthContentHolder,
    AuthTextInput,
    Form,
    ShowPasswordButton,
    PasswordValidationErrorsList
} from "../Login/AuthComponents";

export function ChangePassword({ username, submit, isAdviser = false }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [arePasswordsVisible, setArePasswordsVisible] = useState(false);
    const [message, setMessage] = useState("");

    const [currentState, changeStateTo, { LOADING, ERROR, SUCCESS, DEFAULT }] = useComponentState();

    const resetFormInputFields = useCallback(() => {
        setOldPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        setArePasswordsVisible(false);
    }, []);

    useEffect(() => {
        if (currentState === SUCCESS) {
            setMessage("You have successfully changed your password.");
            resetFormInputFields();
        }
    }, [currentState, SUCCESS, resetFormInputFields, changeStateTo, setMessage]);

    const submitChange = async e => {
        e.preventDefault();

        if (isAdviser) {
            return;
        }

        changeStateTo(LOADING);
        const { status, message } = await submit(oldPassword, newPassword);

        changeStateTo(status === DONE ? SUCCESS : ERROR);
        setMessage(message);
    };

    const togglePasswordVisibility = () => setArePasswordsVisible(v => !v);

    const validationFailures = validate(username, newPassword, confirmNewPassword);

    return (
        <Accordion
            isOpen={isExpanded}
            handleChange={() => {
                setIsExpanded(!isExpanded);
                changeStateTo(DEFAULT);
                resetFormInputFields();
            }}
            title="Change password"
        >
            <StyledAuthContentHolder>
                <MessageContainer>
                    <Message
                        shown={currentState === SUCCESS || currentState === ERROR}
                        text={message}
                        severity={currentState === ERROR ? ERROR_SEVERITY : INFO_SEVERITY}
                    />
                </MessageContainer>
                <StyledForm onSubmit={submitChange} data-testid="change-password-form">
                    <AuthTextInput
                        id="password-input-old"
                        value={oldPassword}
                        label="Old password"
                        type={arePasswordsVisible ? "text" : "password"}
                        onChange={e => setOldPassword(e.target.value)}
                        disabled={currentState === LOADING}
                        InputProps={{
                            endAdornment: (
                                <ShowPasswordButton
                                    handleClickShowPassword={togglePasswordVisibility}
                                    isPasswordVisible={arePasswordsVisible}
                                />
                            )
                        }}
                    />

                    <AuthTextInput
                        id="password-input-new"
                        type={arePasswordsVisible ? "text" : "password"}
                        label="New password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        disabled={currentState === LOADING}
                        InputProps={{
                            endAdornment: (
                                <ShowPasswordButton
                                    handleClickShowPassword={togglePasswordVisibility}
                                    isPasswordVisible={arePasswordsVisible}
                                />
                            )
                        }}
                    />

                    <AuthTextInput
                        id="password-input-confirm"
                        type={arePasswordsVisible ? "text" : "password"}
                        label="Confirm new password"
                        value={confirmNewPassword}
                        onChange={e => setConfirmNewPassword(e.target.value)}
                        disabled={currentState === LOADING}
                        InputProps={{
                            endAdornment: (
                                <ShowPasswordButton
                                    handleClickShowPassword={togglePasswordVisibility}
                                    isPasswordVisible={arePasswordsVisible}
                                />
                            )
                        }}
                    />
                    <StyledContainer>
                        <PasswordValidationErrorsList errors={validationFailures} />
                    </StyledContainer>
                    <StyledLoading size={22} displayWhen={currentState !== LOADING}>
                        <Controls>
                            <ControlBtn
                                data-testid="cancel-password-change"
                                type="reset"
                                onClick={() => {
                                    setIsExpanded(false);
                                    resetFormInputFields();
                                }}
                                color="primary"
                            >
                                Cancel
                            </ControlBtn>
                            <ControlBtn
                                id="submit-password-change"
                                data-testid="submit-password-change"
                                type="submit"
                                value="Submit"
                                disabled={isSubmitDisabled({
                                    oldPassword,
                                    newPassword,
                                    confirmNewPassword,
                                    validationFailures,
                                    isAdviser
                                })}
                                variant="contained"
                                color="primary"
                            >
                                Submit
                            </ControlBtn>
                        </Controls>
                    </StyledLoading>
                </StyledForm>
            </StyledAuthContentHolder>
        </Accordion>
    );
}

const StyledContainer = styled.div`
    padding: 1rem;
`;

const StyledAuthContentHolder = styled(AuthContentHolder)`
    padding: 0;
`;

const MessageContainer = styled.div`
    width: 100%;
    padding: 1rem;
`;

const StyledForm = styled(Form)`
    margin: 1rem 0 0 0;
    text-align: center;
`;

const StyledLoading = styled(Loading)`
    height: 4rem;
    margin-top: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Controls = styled.div`
    margin: 1.25rem auto auto auto;
    max-width: 280px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 4rem;
`;

export const ControlBtn = styled(Button)`
    width: 48%;
`;

ChangePassword.propTypes = {
    submit: PropTypes.func,
    username: PropTypes.string,
    isAdviser: PropTypes.bool
};
